import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { connectorSyncsOnDemand, type Task } from '@app/shared'

import {
  useCalendarRange,
  useClassOccurrences,
  useClock,
  useConnectors,
  useDayTriage,
  useDesktopWorker,
  useNotificationLog,
  useSetMailState,
  useSyncConnector,
  useTasks,
  useToggleTask,
  type NotificationRecord,
} from '../../lib/data'
import { useCapture } from '../shell/CaptureContext'
import { buzz } from '../shell/motion'
import { connectorNames } from '../sync/PullToRefresh'
import { PullToSync } from '../sync/PullToSync'
import { CardSkeleton, MailCard, RemindersCard } from '../day/Cards'
import {
  blockDots,
  chipState,
  classLine,
  dayBlocks,
  freeBlock,
  lastMailCheck,
  notifAt,
  readDismissed,
  laterOf,
  safeStorage,
  seenOnFirstRun,
  sinceWhen,
  syncSummary,
  todaysReminders,
  unreadNotifications,
  visibleNotifications,
  writeDismissed,
  writeSeen,
  type ClassLine,
  type MailRow,
} from '../day/day'
import { Hero, type ChipView } from '../day/Hero'
import { useLiveRecording, useOnline, type LiveRecording } from '../day/hooks'
import { MailSheet } from '../day/MailSheet'
import { NotifSheet } from '../day/NotifSheet'
import { scenarioInputs, useMobileDayScenario } from '../day/scenario'
import { SyncSheet } from '../day/SyncSheet'
import { DayToast, useToast } from '../day/Toast'

// ============================================================================
// DAY — V10 boards 01 (light) and 02 (dark), the app's first screen (owner, 2026-10-18).
//
// Three things, in this order: the hero (what day it is, the next class, the free block and how
// today is going), the mail that needs you, and today's reminders. Every tap goes somewhere —
// the hero to today's calendar, the status chip to whatever it is showing, the mail card to the
// important-mail sheet, a reminder's circle to done (with Undo), the rest of that card to
// Reminders — and pulling the page down runs a real sync and shows what it found.
//
// WHAT LEFT DAY (owner decisions, 2026-10-07/18): the journal composer (it lives in Notes; the
// Notes tab opens the journal and offers today's page — Phase 4 builds the Notes | Journal
// switch), the `Day page · prototype` link (the mail sheet's "All mail" goes there now), and the
// notifications bell (the notifications sheet's gear goes to the same settings screen).
//
// THE HONEST STATES (board 26) are the cards' own: a skeleton of the card's shape while a read is
// out, the failure in words with a Retry, the zero sentence, "as of" while offline, and a status
// chip that says "As of" instead of "Synced" while a connector is in error. `/?state=` opens each
// of them in demo mode (mobile/day/scenario.ts).
// ============================================================================

export function MobileDay() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const capture = useCapture()
  const scenario = useMobileDayScenario()
  const inputs = scenarioInputs(scenario)

  const clock = useClock({ live: true })
  const today = clock.data?.date
  const now = clock.data?.minutesIntoDay ?? 0

  const classesQ = useClassOccurrences(today)
  const range = useMemo(() => (today ? { from: today, to: today } : undefined), [today])
  const rangeQ = useCalendarRange(range)
  const tasksQ = useTasks()
  const triageQuery = useMemo(() => ({ days: 7, ...(inputs.triage ? { scenario: inputs.triage } : {}) }), [inputs.triage])
  const triageQ = useDayTriage(triageQuery)
  const connectorsQ = useConnectors(inputs.connectors)
  const workerQ = useDesktopWorker(inputs.connectors)
  const logQ = useNotificationLog({ status: 'sent', limit: 20 })

  const toggle = useToggleTask()
  const setMailState = useSetMailState()
  const sync = useSyncConnector()
  const { toast, show, clear } = useToast()

  const online = useOnline() && !inputs.forceOffline
  const [recordingFixture] = useState<LiveRecording | null>(() =>
    inputs.recording
      ? { startedAt: new Date(Date.now() - (23 * 60 + 14) * 1000).toISOString(), courseCode: 'COGSCI 1' }
      : null,
  )
  const recording = useLiveRecording(recordingFixture)

  const [sheet, setSheet] = useState<'sync' | 'notif' | 'mail' | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)

  // ── Notifications: the log, minus what this phone dismissed ────────────────────────────────
  const storage = safeStorage()
  const [dismissed, setDismissed] = useState(() => readDismissed(storage))
  // First run on this device: everything already in the log counts as seen (day.ts →
  // seenOnFirstRun). The demo `notification` state keeps no marker, so its fixtures stay new.
  const [seen, setSeen] = useState<string | null>(() =>
    inputs.notifications ? null : seenOnFirstRun(storage, new Date().toISOString()),
  )
  const [hotIds, setHotIds] = useState<ReadonlySet<string>>(() => new Set())
  const [lastClear, setLastClear] = useState<string[] | null>(null)
  const logRows: NotificationRecord[] = inputs.notifications ?? logQ.data ?? []
  const visible = useMemo(() => visibleNotifications(logRows, dismissed), [logRows, dismissed])
  const unread = useMemo(() => unreadNotifications(visible, seen), [visible, seen])

  const saveDismissed = (next: Set<string>) => {
    setDismissed(next)
    writeDismissed(storage, next)
  }

  const openNotifications = () => {
    setHotIds(new Set(unread.map((n) => n.id)))
    const newest = visible[0]
    if (newest) {
      const at = laterOf(seen, notifAt(newest))
      setSeen(at)
      if (!inputs.notifications) writeSeen(storage, at)
    }
    setLastClear(null)
    setSheet('notif')
  }

  // ── Reminders ───────────────────────────────────────────────────────────────────────────────
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set())
  const tasks = inputs.noReminders ? [] : tasksQ.data
  const plate = useMemo(() => (tasks && today ? todaysReminders(tasks, today) : null), [tasks, today])
  const openToday = plate ? plate.open.filter((t) => !hidden.has(t.id)) : null

  const complete = (task: Task) => {
    buzz(8)
    setHidden((s) => new Set(s).add(task.id))
    const unhide = () =>
      setHidden((s) => {
        const n = new Set(s)
        n.delete(task.id)
        return n
      })
    toggle.mutate(
      { id: task.id, done: true },
      {
        onError: (e) => {
          unhide()
          show({ text: `Not done — ${e instanceof Error ? e.message : 'the change did not go through'}` })
        },
      },
    )
    show({
      text: `Done · ${task.title}`,
      undo: () => {
        toggle.mutate({ id: task.id, done: false })
        unhide()
      },
    })
  }

  // ── Mail ────────────────────────────────────────────────────────────────────────────────────
  const [handled, setHandled] = useState<ReadonlySet<string>>(() => new Set())
  const unhandle = (id: string) =>
    setHandled((s) => {
      const n = new Set(s)
      n.delete(id)
      return n
    })

  const handle = (row: MailRow) => {
    buzz(10)
    setHandled((s) => new Set(s).add(row.id))
    setMailState.mutate(
      { id: row.id, state: 'done' },
      {
        onError: (e) => {
          unhandle(row.id)
          show({ text: `Not marked — ${e instanceof Error ? e.message : 'the change did not go through'}` })
        },
      },
    )
    show({
      text: 'Marked handled — hidden from Day',
      undo: () => {
        setMailState.mutate({ id: row.id, state: 'open' })
        unhandle(row.id)
      },
    })
  }

  const undoAllMail = () => {
    for (const id of handled) setMailState.mutate({ id, state: 'open' })
    setHandled(new Set())
    clear()
  }

  const triage = triageQ.data
  const canOpenMail = Boolean(triage && triage.accounts.length > 0)

  // ── Sync ────────────────────────────────────────────────────────────────────────────────────
  const summary = syncSummary(connectorsQ.data)
  const names = connectorNames(connectorsQ.data)

  const runSync = useCallback(async () => {
    setSyncing(true)
    setRefreshError(null)
    try {
      const live = (connectorsQ.data ?? []).filter(connectorSyncsOnDemand)
      const results = await Promise.allSettled(live.map((c) => sync.mutateAsync(c.id)))
      const failed = results.find((r) => r.status === 'rejected')
      if (failed && failed.status === 'rejected') {
        setRefreshError(failed.reason instanceof Error ? failed.reason.message : 'The check did not go through.')
      }
      await queryClient.invalidateQueries()
    } finally {
      setSyncing(false)
    }
  }, [connectorsQ.data, queryClient, sync])

  // ── The hero's inputs ───────────────────────────────────────────────────────────────────────
  const line: ClassLine | null = classesQ.error
    ? { kind: 'none', eyebrow: 'Class times could not be read' }
    : classesQ.data
      ? classLine(classesQ.data, now)
      : null
  const spans = classesQ.data && rangeQ.data && today ? dayBlocks(classesQ.data, rangeQ.data.blocks, today) : null
  const calendarFailed = Boolean(classesQ.error || rangeQ.error)

  const state = chipState({ recording: recording !== null, unread: unread.length, online })
  const chip: ChipView =
    state === 'recording' && recording
      ? { kind: 'recording', recording }
      : state === 'notification'
        ? { kind: 'notification', count: unread.length, title: unread[0]!.title, onOpen: openNotifications }
        : state === 'off'
          ? { kind: 'off' }
          : syncing
            ? { kind: 'syncing' }
            : {
                kind: 'synced',
                stale: summary.stale,
                text: connectorsQ.error
                  ? 'Sync report'
                  : summary.latest
                    ? `${summary.stale ? 'As of' : 'Synced'} ${sinceWhen(summary.latest, today)}`
                    : 'Not synced yet',
                onOpen: () => {
                  setRefreshError(null)
                  setSheet('sync')
                },
              }

  const loading = inputs.forceLoading
  const tasksAsOf = !online && tasksQ.dataUpdatedAt > 0 ? new Date(tasksQ.dataUpdatedAt).toISOString() : null

  return (
    <div className="m-screen-ground flex min-h-0 flex-1 flex-col bg-m-ground font-m text-m-ink">
      <PullToSync
        onSync={runSync}
        onSynced={() => setSheet('sync')}
        label={names ? `Checking ${names}…` : 'Checking — no connectors are set up yet…'}
      >
        <div
          className="flex flex-col gap-3 px-[14px] pb-5"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 10px)' }}
        >
          {today ? (
            <Hero
              today={today}
              classLine={line}
              dots={spans ? blockDots(spans, now) : null}
              free={calendarFailed ? 'error' : spans ? freeBlock(spans, now) : null}
              progress={plate ? { done: plate.done.length, open: openToday?.length ?? 0 } : null}
              chip={chip}
            />
          ) : (
            <div aria-hidden className="h-[292px] flex-none animate-pulse rounded-[36px] bg-m-accent opacity-60" />
          )}

          <MailCard
            today={today}
            triage={triage}
            error={triageQ.error}
            loading={loading || triageQ.isPending}
            asOf={!online && triage ? lastMailCheck(triage) : null}
            onOpen={() => {
              if (canOpenMail) {
                setHandled(new Set())
                setSheet('mail')
              }
            }}
            onRetry={() => void triageQ.refetch()}
            onSyncGmail={() =>
              sync.mutate('gmail', {
                onError: (e) => show({ text: e instanceof Error ? e.message : 'Gmail did not answer.' }),
              })
            }
            syncingGmail={sync.isPending}
          />

          {today ? (
            <RemindersCard
              today={today}
              open={openToday}
              loading={loading || (tasksQ.isPending && !inputs.noReminders)}
              error={tasksQ.error}
              asOf={tasksAsOf}
              onRetry={() => void tasksQ.refetch()}
              onComplete={complete}
            />
          ) : (
            <CardSkeleton slab height={190} />
          )}
        </div>
      </PullToSync>

      <SyncSheet
        open={sheet === 'sync'}
        onClose={() => setSheet(null)}
        connectors={connectorsQ.data}
        connectorsError={connectorsQ.error}
        worker={workerQ.data}
        refreshError={refreshError}
        today={today}
        onNotifications={openNotifications}
      />

      {today && (
        <NotifSheet
          open={sheet === 'notif'}
          onClose={() => setSheet(null)}
          today={today}
          rows={visible}
          unread={hotIds}
          loading={!inputs.notifications && logQ.isPending}
          error={inputs.notifications ? null : logQ.error}
          canUndo={lastClear !== null && lastClear.length > 0}
          onDismiss={(id) => saveDismissed(new Set(dismissed).add(id))}
          onClearAll={() => {
            buzz(6)
            const ids = visible.map((n) => n.id)
            setLastClear(ids)
            saveDismissed(new Set([...dismissed, ...ids]))
          }}
          onUndo={() => {
            const back = new Set(dismissed)
            for (const id of lastClear ?? []) back.delete(id)
            setLastClear(null)
            saveDismissed(back)
          }}
          onOpenRow={(n) => {
            setSheet(null)
            navigate(n.url)
          }}
          onSettings={() => {
            setSheet(null)
            navigate('/settings/notifications')
          }}
        />
      )}

      {today && (
        <MailSheet
          open={sheet === 'mail'}
          onClose={() => setSheet(null)}
          today={today}
          triage={triage}
          handled={handled}
          onHandle={handle}
          onUndoAll={undoAllMail}
          onRemind={(row) => {
            setSheet(null)
            capture.openReminder(row.subject)
          }}
        />
      )}

      <DayToast toast={toast} overSheet={sheet !== null} onDone={clear} />
    </div>
  )
}
